import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { campaignsService } from '../services/campaignsService';
import type {
  CreateCampaignRequest,
  UpdateCampaignRequest,
  CampaignQuery,
} from '@troia-v3/types';

/**
 * Hook to fetch campaigns with pagination and filters
 */
export const useCampaigns = (query?: CampaignQuery) => {
  return useQuery({
    queryKey: ['campaigns', query],
    queryFn: () => campaignsService.getCampaigns(query),
    staleTime: 30 * 1000, // 30 seconds
  });
};

/**
 * Hook to fetch single campaign by ID
 */
export const useCampaign = (id?: string) => {
  return useQuery({
    queryKey: ['campaign', id],
    queryFn: () => campaignsService.getCampaignById(id!),
    enabled: !!id,
  });
};

/**
 * Hook to create campaign
 */
export const useCreateCampaign = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateCampaignRequest) => campaignsService.createCampaign(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
    },
  });
};

/**
 * Hook to update campaign
 */
export const useUpdateCampaign = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateCampaignRequest }) =>
      campaignsService.updateCampaign(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      queryClient.invalidateQueries({ queryKey: ['campaign', variables.id] });
    },
  });
};

/**
 * Hook to delete campaign
 */
export const useDeleteCampaign = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => campaignsService.deleteCampaign(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
    },
  });
};

/**
 * Hook to duplicate a campaign (backend cria uma cópia como DRAFT)
 */
export const useDuplicateCampaign = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => campaignsService.duplicateCampaign(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
    },
  });
};

/**
 * Hook to update campaign status
 */
export const useUpdateCampaignStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      campaignsService.updateCampaignStatus(id, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
    },
  });
};

/**
 * Hook to get campaigns by status
 */
export const useCampaignsByStatus = (status: string) => {
  return useQuery({
    queryKey: ['campaigns', 'status', status],
    queryFn: () => campaignsService.getCampaignsByStatus(status),
    enabled: !!status,
  });
};

/**
 * Hook to activate campaign (draft → scheduled)
 */
export const useActivateCampaign = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => campaignsService.activateCampaign(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      queryClient.invalidateQueries({ queryKey: ['campaign', id] });
    },
  });
};

/**
 * Hook to pause campaign (in_progress → paused)
 */
export const usePauseCampaign = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => campaignsService.pauseCampaign(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      queryClient.invalidateQueries({ queryKey: ['campaign', id] });
    },
  });
};

/**
 * Hook to resume campaign (paused → in_progress)
 */
export const useResumeCampaign = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => campaignsService.resumeCampaign(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      queryClient.invalidateQueries({ queryKey: ['campaign', id] });
    },
  });
};

/**
 * Hook to cancel campaign (scheduled/in_progress/paused → cancelled)
 */
export const useCancelCampaign = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => campaignsService.cancelCampaign(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      queryClient.invalidateQueries({ queryKey: ['campaign', id] });
    },
  });
};

/**
 * Hook to fetch campaign stats
 */
export const useCampaignStats = (id?: string) => {
  return useQuery({
    queryKey: ['campaign-stats', id],
    queryFn: () => campaignsService.getCampaignStats(id!),
    enabled: !!id,
    refetchInterval: 10000, // Auto-refresh every 10s for active campaigns
  });
};

/**
 * Hook to fetch campaign messages with pagination and filters
 */
export const useCampaignMessages = (id?: string, query?: {
  page?: number;
  limit?: number;
  status?: string;
  search?: string;
}) => {
  return useQuery({
    queryKey: ['campaign-messages', id, query],
    queryFn: () => campaignsService.getCampaignMessages(id!, query),
    enabled: !!id,
    staleTime: 5000,
  });
};

/**
 * Hook to send test message
 */
export const useSendTestMessage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: {
      id: string;
      data: {
        testRecipients: string[];
        variableMapping: Record<number, { source: string; fieldName?: string; staticValue?: string }>;
      };
    }) => campaignsService.sendTestMessage(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaign-stats'] });
    },
  });
};
